/**
 * CertificateCreatePage — iter-217c-v3
 *
 * MAJOR REDESIGN per user (Feb 2026):
 *   • Removes the legalese wrapper. New opening for BOTH types:
 *       AR: «نحن أسرة كلاود جي إم …»
 *       EN: «At CLOUD GM …»
 *   • Two-column PDF: AR on the right, EN on the left (mirror-image
 *     bilingual layout — mirrors how the admin fills it in the form).
 *   • Congratulation type has NO title — just the family opening + the
 *     recipient's name + the pre-filled narrative.
 *   • To-Whom title becomes «لمن يهمه الأمر» (no wording of "شهادة").
 *   • Picking a subject auto-pre-fills TWO editable body boxes
 *     (AR + EN) from the subject's stored narrative_ar / narrative_en.
 *   • Footer:
 *       - Both sites (cloudgm.com, cloudgm.business)
 *       - Both info emails (info@cloudgm.com, info@cloudgm.business)
 *       - Phone rendered LTR (+971 first, then digits)
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Award, Loader2, Send, Search, User, Building2, Store, Users,
  Briefcase, CheckCircle2, ChevronLeft, ChevronRight, Languages, Type,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL   = '/pdf-logo-cloudgm.png';

// Same brand palette as CompanyMarketingEditor.
const BRAND = {
  blue:   '#3B9EDC',
  blue700:'#0369a1',
  orange: '#F5A55A',
  orange700:'#d97706',
  ink:    '#0f172a',
  ink600: '#334155',
  ink400: '#64748b',
  paper:  '#ffffff',
  tint:   '#f8fafc',
  gold:   '#d4af37',
  gradient: 'linear-gradient(90deg, #3B9EDC 0%, #F5A55A 100%)',
};

const CONTACT = {
  sites:  ['cloudgm.com', 'cloudgm.business'],
  phone:  '+971 50 151 1917',
  emails: ['info@cloudgm.com', 'info@cloudgm.business'],
};

const CATEGORY_META = {
  merchant: { ar: 'منصة التجار',  en: 'Merchants',  icon: Store,      color: '#8B5CF6' },
  customer: { ar: 'منصة العملاء', en: 'Customers',  icon: Users,      color: '#1E9BD9' },
  company:  { ar: 'للشركة',        en: 'Company',    icon: Building2,  color: '#F59E0B' },
  employee: { ar: 'للموظفين',      en: 'Employees',  icon: Briefcase,   color: '#10b981' },
};

const CERT_TYPES = [
  { id: 'to_whom',        ar: 'شهادة لمن يهمه الأمر',  en: 'To Whom It May Concern' },
  { id: 'congratulation', ar: 'شهادة تهنئة',           en: 'Congratulations' },
];

// Default opening lines when the picked subject has NO narrative stored.
const DEFAULT_OPENERS = {
  congratulation: {
    ar: (name, subj) => `نحن أسرة كلاود جي إم نتقدّم إلى ${name || '—'} بأصدق التهاني وأطيب التمنيات ${subj ? `بمناسبة: ${subj}` : ''}. متمنّين لكم دوام التوفيق والنجاح في مسيرتكم القادمة.`,
    en: (name, subj) => `At CLOUD GM, we extend our warmest congratulations to ${name || '—'} ${subj ? `on the occasion of: ${subj}` : ''}. Wishing you continued success and prosperity in your journey ahead.`,
  },
  to_whom: {
    ar: (name, subj) => `نحن أسرة كلاود جي إم نشهد بأن ${name || '—'} ${subj ? `يخصّه الموضوع التالي: ${subj}` : ''}. تُصدَر هذه الشهادة بناءً على طلب صاحب العلاقة.`,
    en: (name, subj) => `At CLOUD GM, we hereby certify that ${name || '—'} ${subj ? `is concerned with the following matter: ${subj}` : ''}. This certificate is issued at the request of the concerned party.`,
  },
};


export default function CertificateCreatePage() {
  const { language } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const { category } = useParams();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const Arrow = isAr ? ChevronLeft : ChevronRight;

  const authHeaders = useCallback(() => getAuthHeaders(), [getAuthHeaders]);

  const meta = CATEGORY_META[category] || CATEGORY_META.company;
  const CatIcon = meta.icon;

  // ── State ───────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [company, setCompany] = useState({ name_ar: '', name_en: '' });
  const [subjects, setSubjects] = useState([]);

  // Recipient — iter-218l (Feb 2026): title prefix + always-dual AR/EN names.
  const [recipientMode, setRecipientMode] = useState(
    category === 'company' ? 'manual' : (category === 'employee' ? 'staff' : 'registered')
  );
  const [recipient, setRecipient] = useState({
    id: '', name: '',
    name_ar: '', name_en: '',
    title_ar: '', title_en: '',
    email: '', phone: '',
  });
  const [searchQ, setSearchQ]     = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // International courtesy titles — synced AR + EN. When user picks one,
  // both languages update together to keep the doc consistent.
  const RECIPIENT_TITLES = [
    { en: 'Mr.',   ar: 'السيد' },
    { en: 'Mrs.',  ar: 'السيدة' },
    { en: 'Ms.',   ar: 'الآنسة' },
    { en: 'Miss',  ar: 'الآنسة' },
    { en: 'Dr.',   ar: 'الدكتور' },
    { en: 'Prof.', ar: 'الأستاذ' },
    { en: 'Sir',   ar: 'السيد المحترم' },
    { en: 'Madam', ar: 'السيدة المحترمة' },
    { en: 'H.E.',  ar: 'سعادة' },
    { en: 'Sh.',   ar: 'الشيخ' },
    { en: 'Shk.',  ar: 'الشيخة' },
  ];
  const applyTitle = (idx) => {
    if (idx === '' || idx === null || idx === undefined) {
      setRecipient(r => ({ ...r, title_ar: '', title_en: '' }));
      return;
    }
    const t = RECIPIENT_TITLES[Number(idx)];
    if (t) setRecipient(r => ({ ...r, title_ar: t.ar, title_en: t.en }));
  };

  // Certificate form
  const [certType, setCertType]     = useState('congratulation');
  const [subjectId, setSubjectId]   = useState('');
  const [certDate, setCertDate]     = useState(new Date().toISOString().split('T')[0]);
  const [certNo, setCertNo]         = useState(`CG-${new Date().getFullYear()}-${Math.floor(Math.random() * 900000 + 100000)}`);

  // iter-217c-v3 — Two editable narrative boxes. When the admin picks a
  // subject from the dropdown, both boxes are pre-filled from the subject's
  // stored `narrative_ar` / `narrative_en` (with a fallback opener when the
  // subject has no narrative saved).
  const [bodyAr, setBodyAr] = useState('');
  const [bodyEn, setBodyEn] = useState('');

  // iter-218r (Feb 2026) — Typography controls for the certificate body.
  // Some templates run long and overflow the single A4 page; admins can now
  // tune font family / size / line-height so the text fills the page cleanly.
  // Applied to BOTH AR + EN body paragraphs in the preview (and therefore
  // to the generated PDF because it snapshots the preview DOM).
  const [bodyFontFamily, setBodyFontFamily] = useState('Cairo');
  const [bodyFontSize,   setBodyFontSize]   = useState(15); // px
  const [bodyLineHeight, setBodyLineHeight] = useState(1.9);
  // Compose the actual CSS font-stack from the picked base family so system
  // fallbacks stay solid on all platforms/PDF engines.
  const bodyFontStack = useMemo(() => {
    const map = {
      Cairo:            '"Cairo","Tajawal",-apple-system,BlinkMacSystemFont,sans-serif',
      Tajawal:          '"Tajawal","Cairo",-apple-system,BlinkMacSystemFont,sans-serif',
      Amiri:            '"Amiri","Cairo","Times New Roman",serif',
      'Noto Naskh':     '"Noto Naskh Arabic","Amiri","Cairo",serif',
      'Times New Roman':'"Times New Roman","Amiri","Cairo",serif',
      Arial:            'Arial,"Cairo","Tajawal",sans-serif',
    };
    return map[bodyFontFamily] || map.Cairo;
  }, [bodyFontFamily]);

  // Small translate helper for the two body boxes (admin might have written
  // only one language while composing).
  const [translating, setTranslating] = useState({});

  // iter-217c-v17 — approved-signature images. Empty during draft creation;
  // filled from the certificate record after admin approves in Phase 3
  // (SignaturesApprovals → Certificate approvals).
  const [approvedSignatureImg, setApprovedSignatureImg] = useState(null);
  const [approvedStampImg, setApprovedStampImg]         = useState(null);

  const previewRef = useRef(null);

  // ── Load reference data ─────────────────────────────────────────
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [subsRes, compRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/certificates/subjects?category=${category}&active_only=true`,
                  { headers: authHeaders() }),
        axios.get(`${BACKEND_URL}/api/admin/certificates/company-name`,
                  { headers: authHeaders() }),
      ]);
      setSubjects(subsRes.data.data || []);
      setCompany({
        name_ar: compRes.data.name_ar || 'شركة كلاود جي إم إف زد ذ.م.م',
        name_en: compRes.data.name_en || 'CLOUDGM FZ LLC',
      });
    } catch (e) {
      toast.error(t('فشل التحميل', 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [category, authHeaders]);

  useEffect(() => { window.scrollTo(0, 0); loadAll(); }, [loadAll]);

  // ── Recipient search ────────────────────────────────────────────
  const doSearch = useCallback(async () => {
    if (searchQ.trim().length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      if (category === 'employee') {
        const r = await axios.get(`${BACKEND_URL}/api/admin/assigned-tasks/staff`, { headers: authHeaders() });
        const rows = (r.data.data || []).filter(u =>
          (u.name || '').toLowerCase().includes(searchQ.toLowerCase()) ||
          (u.email || '').toLowerCase().includes(searchQ.toLowerCase())
        );
        setSearchResults(rows);
      } else if (category === 'merchant' || category === 'customer') {
        const type = category === 'customer' ? 'customer' : 'agent';
        const r = await axios.get(
          `${BACKEND_URL}/api/admin/assigned-tasks/targets/search?type=${type}&q=${encodeURIComponent(searchQ.trim())}`,
          { headers: authHeaders() },
        );
        setSearchResults(r.data.data || []);
      }
    } catch (e) {/* silent */}
    finally { setSearching(false); }
  }, [searchQ, category, authHeaders]);

  useEffect(() => {
    if (recipientMode !== 'registered' && recipientMode !== 'staff') return;
    const to = setTimeout(doSearch, 300);
    return () => clearTimeout(to);
  }, [searchQ, doSearch, recipientMode]);

  // ── Auto-fill narratives when subject changes ───────────────────
  const selectedSubject = useMemo(() => subjects.find(s => s.id === subjectId) || null, [subjects, subjectId]);

  useEffect(() => {
    // Pre-fill both body boxes when a subject is picked. If the subject has
    // stored narratives, use them verbatim (admin can still edit). Otherwise
    // fall back to a generic opener that includes the recipient's name and
    // the subject label.
    if (!selectedSubject) return;
    const openers = DEFAULT_OPENERS[certType] || DEFAULT_OPENERS.congratulation;
    setBodyAr(
      selectedSubject.narrative_ar?.trim()
        ? selectedSubject.narrative_ar
        : openers.ar(recipient.name, selectedSubject.subject_ar || ''),
    );
    setBodyEn(
      selectedSubject.narrative_en?.trim()
        ? selectedSubject.narrative_en
        : openers.en(recipient.name, selectedSubject.subject_en || selectedSubject.subject_ar || ''),
    );
    // NOTE: we deliberately DO NOT reset bodies on recipient change — the
    // admin might have already edited them.
  }, [subjectId, certType]);

  const canPreview = !!recipient.name.trim() && (!!bodyAr.trim() || !!bodyEn.trim());

  // ── Translate body helper ───────────────────────────────────────
  const translateBody = async (direction) => {
    const [sLang, tLang] = direction === 'ar2en' ? ['ar', 'en'] : ['en', 'ar'];
    const src = direction === 'ar2en' ? bodyAr : bodyEn;
    if (!src.trim()) return;
    const key = `body-${direction}`;
    setTranslating(m => ({ ...m, [key]: true }));
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/utils/translate`,
        { text: src.trim(), source: sLang, target: tLang },
        { headers: authHeaders() },
      );
      if (r.data?.translated) {
        direction === 'ar2en' ? setBodyEn(r.data.translated) : setBodyAr(r.data.translated);
        toast.success(t('✓ ترجم', '✓ Translated'));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل الترجمة', 'Translation failed'));
    } finally {
      setTranslating(m => ({ ...m, [key]: false }));
    }
  };

  // ── Submit for approval (iter-217c-v17)
  // Replaces the old client-side PDF download. Sends the whole certificate
  // data to the backend; it lands in the pending-approval queue where an
  // admin approves it. After approval the record contains the signature +
  // stamp data URLs so future re-renders match what was signed.
  const submitForApproval = async () => {
    // iter-218l (Feb 2026) — ALL recipient modes (except company) require
    // BOTH names in AR + EN + a title prefix.
    if (category !== 'company') {
      if (!recipient.name_ar?.trim()) {
        toast.error(t('أدخل الاسم بالعربية', 'Enter the Arabic name'));
        return;
      }
      if (!recipient.name_en?.trim()) {
        toast.error(t('أدخل الاسم بالإنجليزية', 'Enter the English name'));
        return;
      }
      if (!recipient.title_ar || !recipient.title_en) {
        toast.error(t('اختر لقب المستلم (السيد/السيدة/الآنسة…)', 'Pick recipient title (Mr./Mrs./Miss…)'));
        return;
      }
    } else if (!recipient.name?.trim()) {
      toast.error(t('أدخل اسم الشركة المستلمة', 'Enter the recipient company name'));
      return;
    }
    if (!(bodyAr.trim() || bodyEn.trim())) {
      toast.error(t('اكتب نص السرد أولاً', 'Write the certificate body first'));
      return;
    }
    setSaving(true);
    try {
      const isCompany = category === 'company';
      const primaryName = isCompany
        ? recipient.name.trim()
        : (recipient.name_ar || recipient.name_en);
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/certificates/pending`, {
        category,
        cert_type:         certType,
        cert_no:           certNo,
        cert_date:         certDate,
        subject_id:        subjectId || null,
        subject_ar:        selectedSubject?.subject_ar || '',
        subject_en:        selectedSubject?.subject_en || selectedSubject?.subject_ar || '',
        recipient_id:      recipient.id || null,
        recipient_name:    primaryName,
        recipient_name_ar: isCompany ? null : recipient.name_ar.trim(),
        recipient_name_en: isCompany ? null : recipient.name_en.trim(),
        recipient_title_ar: isCompany ? null : (recipient.title_ar || null),
        recipient_title_en: isCompany ? null : (recipient.title_en || null),
        recipient_email:   recipient.email || null,
        recipient_phone:   recipient.phone || null,
        recipient_mode:    recipientMode,
        body_ar:           bodyAr,
        body_en:           bodyEn,
        // iter-218r (Feb 2026) — Typography settings ride along with the
        // pending doc so the final approved PDF renders with the exact
        // font/size/line-height the admin picked during composition.
        body_font_family:  bodyFontFamily,
        body_font_size:    bodyFontSize,
        body_line_height:  bodyLineHeight,
        company_name_ar:   company.name_ar || '',
        company_name_en:   company.name_en || '',
      }, { headers: authHeaders() });
      toast.success(t(
        `تم الإرسال للاعتماد — رقم الشهادة ${data.id}`,
        `Sent for approval — cert ${data.id}`,
      ));
      navigate('/manage/cloudgm-fz-llc/certificates');
    } catch (err) {
      toast.error(err?.response?.data?.detail
        || err?.message
        || t('فشل الإرسال للاعتماد', 'Failed to send for approval'));
    } finally {
      setSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────
  return (
    <DashboardLayout
      title={`إصدار شهادة — ${meta.ar}`}
      titleEn={`Issue Certificate — ${meta.en}`}
      subtitle="تُوقَّع تلقائياً بشعار CloudGM وألوانها"
      subtitleEn="Auto-branded with CloudGM logo & colors"
      screenCode="FZLC-CERT-NEW"
      backPath="/manage/cloudgm-fz-llc/certificates"
      onBack={() => navigate('/manage/cloudgm-fz-llc/certificates')}
      breadcrumb={[
        { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
        { labelAr: 'إدارة شركة CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC Management', path: '/manage/cloudgm-fz-llc' },
        { labelAr: 'الشهادات', labelEn: 'Certificates', path: '/manage/cloudgm-fz-llc/certificates' },
        { labelAr: meta.ar, labelEn: meta.en },
      ]}
    >
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6" dir={isAr ? 'rtl' : 'ltr'} data-testid={`cert-create-${category}`}>
        {loading ? (
          <div className="p-16 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-white/40" /></div>
        ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* ── Left: Form ─────────────────────────────────────────── */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
            {/* Category badge */}
            <div className="flex items-center gap-3 p-4 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                   style={{ backgroundColor: `${meta.color}15`, border: `1px solid ${meta.color}40` }}>
                <CatIcon className="w-5 h-5" style={{ color: meta.color }} />
              </div>
              <div>
                <p className="text-white font-semibold">{isAr ? meta.ar : meta.en}</p>
                <p className="text-xs text-white/50">{t('فئة المستلم', 'Recipient category')}</p>
              </div>
            </div>

            {/* Certificate type */}
            <section className="p-5 rounded-2xl bg-white/[0.02] border border-white/10">
              <label className="block text-sm font-semibold text-white mb-3">{t('نوع الشهادة', 'Certificate Type')} *</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {CERT_TYPES.map(ct => (
                  <button
                    key={ct.id}
                    type="button"
                    onClick={() => setCertType(ct.id)}
                    className={`h-11 rounded-xl text-sm font-medium border transition ${
                      certType === ct.id
                        ? 'bg-emerald-500/20 text-emerald-100 border-emerald-500/50'
                        : 'bg-[#0a0a0a] text-white/70 border-white/10 hover:border-white/30'
                    }`}
                    data-testid={`cert-type-${ct.id}`}
                  >
                    {isAr ? ct.ar : ct.en}
                  </button>
                ))}
              </div>
            </section>

            {/* Subject */}
            <section className="p-5 rounded-2xl bg-white/[0.02] border border-white/10">
              <label className="block text-sm font-semibold text-white mb-3">
                {t('موضوع الشهادة', 'Subject')} *
              </label>
              {subjects.length > 0 ? (
                <select
                  value={subjectId}
                  onChange={(e) => setSubjectId(e.target.value)}
                  className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 h-11 text-white text-sm"
                  style={{ colorScheme: 'dark' }}
                  data-testid="subject-select"
                >
                  <option value="">{t('— اختر من القائمة —', '— Choose from list —')}</option>
                  {subjects.map(s => (
                    <option key={s.id} value={s.id}>{isAr ? s.subject_ar : (s.subject_en || s.subject_ar)}</option>
                  ))}
                </select>
              ) : (
                <p className="text-[11px] text-amber-400">
                  {t('لا توجد مواضيع محفوظة. أضفها من: التحكم بإدارة الشركة ▸ الشهادات.',
                     'No saved subjects. Add them from: Company Management Control ▸ Certificates.')}
                </p>
              )}
            </section>

            {/* Recipient — iter-218l (Feb 2026):
                • Title dropdown (Mr/Mrs/Miss/Sir/Dr...) — mandatory.
                • Dual AR + EN name fields for ALL modes (except company).
                • Picking a registered customer auto-fills whichever
                  language is available; the missing one MUST be typed
                  manually — no partial submission allowed. */}
            <section className="p-5 rounded-2xl bg-white/[0.02] border border-white/10">
              <label className="block text-sm font-semibold text-white mb-3">{t('المستلم', 'Recipient')} *</label>
              {category !== 'company' && (
                <div className="inline-flex bg-white/5 border border-white/10 rounded-lg p-0.5 text-xs mb-3">
                  {category === 'employee' ? (
                    <button
                      type="button"
                      onClick={() => { setRecipientMode('staff'); setRecipient({ id: '', name: '', name_ar: '', name_en: '', title_ar: '', title_en: '', email: '', phone: '' }); }}
                      className={`px-3 py-1.5 rounded-md font-medium ${recipientMode === 'staff' ? 'bg-blue-500/20 text-blue-200 border border-blue-500/40' : 'text-white/60'}`}
                    >
                      {t('اختر موظفاً مسجّلاً', 'Pick registered employee')}
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => { setRecipientMode('registered'); setRecipient({ id: '', name: '', name_ar: '', name_en: '', title_ar: '', title_en: '', email: '', phone: '' }); }}
                        className={`px-3 py-1.5 rounded-md font-medium ${recipientMode === 'registered' ? 'bg-blue-500/20 text-blue-200 border border-blue-500/40' : 'text-white/60'}`}
                        data-testid="recipient-mode-registered"
                      >
                        {t('عميل مسجّل', 'Registered')}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setRecipientMode('manual'); setRecipient({ id: '', name: '', name_ar: '', name_en: '', title_ar: '', title_en: '', email: '', phone: '' }); }}
                        className={`px-3 py-1.5 rounded-md font-medium ${recipientMode === 'manual' ? 'bg-amber-500/20 text-amber-200 border border-amber-500/40' : 'text-white/60'}`}
                        data-testid="recipient-mode-manual"
                      >
                        {t('غير مسجّل — اكتب الاسم', 'Unregistered — enter name')}
                      </button>
                    </>
                  )}
                </div>
              )}

              {(recipientMode === 'registered' || recipientMode === 'staff') && (
                <>
                  <div className="relative">
                    <Search className="absolute inset-y-0 start-3 my-auto w-4 h-4 text-white/40" />
                    <input
                      value={searchQ}
                      onChange={(e) => setSearchQ(e.target.value)}
                      placeholder={t('ابحث بالاسم / الإيميل / الهاتف…', 'Search by name / email / phone…')}
                      className="w-full bg-[#0a0a0a] border border-white/10 rounded-md h-10 ps-9 pe-3 text-sm text-white"
                      data-testid="recipient-search-input"
                    />
                  </div>
                  {searching && <div className="mt-2 text-xs text-white/50"><Loader2 className="w-3 h-3 animate-spin inline" /> {t('جاري البحث…', 'Searching…')}</div>}
                  {searchResults.length > 0 && (
                    <div className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-white/10 divide-y divide-white/5">
                      {searchResults.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => {
                            // Auto-populate whichever language(s) exist on the row.
                            const arGuess = u.name_ar || (/[\u0600-\u06FF]/.test(u.name || '') ? u.name : '');
                            const enGuess = u.name_en || (/^[A-Za-z\s0-9.\-']+$/.test(u.name || '') ? u.name : '');
                            setRecipient(r => ({
                              ...r,
                              id: u.id,
                              name: u.name || '',
                              name_ar: (arGuess || '').trim(),
                              name_en: (enGuess || '').trim(),
                              email: u.email || '',
                              phone: u.phone || '',
                            }));
                            setSearchResults([]);
                            setSearchQ(u.name || '');
                          }}
                          className="w-full text-start px-3 py-2 hover:bg-white/5 flex flex-col gap-0.5"
                          data-testid={`recipient-search-result-${u.id}`}
                        >
                          <span className="text-sm text-white">{u.name || '—'}</span>
                          <span className="text-[11px] text-white/50" dir="ltr">{u.email || u.phone || u.id}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Common editor: title dropdown + dual AR/EN name fields.
                  Shown for manual mode, company mode, AND after picking
                  a registered customer (so admin can complete missing
                  language). */}
              {(recipientMode === 'manual' || category === 'company' || recipient.id) && (
                <div className="mt-3 space-y-3">
                  {/* Title dropdown — mandatory for non-company categories */}
                  {category !== 'company' && (
                    <div>
                      <label className="block text-[11px] text-white/50 mb-1">
                        {t('اللقب', 'Title')} *
                      </label>
                      <select
                        value={(() => {
                          const idx = RECIPIENT_TITLES.findIndex(
                            t => t.en === recipient.title_en && t.ar === recipient.title_ar,
                          );
                          return idx >= 0 ? String(idx) : '';
                        })()}
                        onChange={(e) => applyTitle(e.target.value)}
                        className="w-full bg-[#0a0a0a] border border-white/10 rounded-md h-11 px-3 text-sm text-white"
                        data-testid="recipient-title-select"
                      >
                        <option value="">{t('— اختر اللقب —', '— Pick a title —')}</option>
                        {RECIPIENT_TITLES.map((tt, i) => (
                          <option key={i} value={String(i)}>
                            {tt.en} — {tt.ar}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {category !== 'company' ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] text-white/50 mb-1">
                          {t('الاسم بالعربية', 'Name (Arabic)')} *
                          {recipient.id && !recipient.name_ar && (
                            <span className="ms-2 text-amber-300">
                              {t('(مطلوب — العميل المسجّل لم يُدرج اسمه بالعربية)', '(required — registered customer has no Arabic name)')}
                            </span>
                          )}
                        </label>
                        <Input
                          value={recipient.name_ar}
                          onChange={(e) => {
                            const cleaned = e.target.value.replace(/[^\u0600-\u06FF\s0-9.\-]/g, '');
                            setRecipient(r => ({ ...r, name_ar: cleaned, name: cleaned || r.name_en || r.name }));
                          }}
                          placeholder={t('الاسم بالعربية فقط', 'Arabic characters only')}
                          className="bg-[#0a0a0a] border-white/10 text-white h-11"
                          dir="rtl"
                          data-testid="recipient-name-ar-input"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-white/50 mb-1">
                          {t('الاسم بالإنجليزية', 'Name (English)')} *
                          {recipient.id && !recipient.name_en && (
                            <span className="ms-2 text-amber-300">
                              {t('(مطلوب — العميل المسجّل لم يُدرج اسمه بالإنجليزية)', '(required — registered customer has no English name)')}
                            </span>
                          )}
                        </label>
                        <Input
                          value={recipient.name_en}
                          onChange={(e) => {
                            const cleaned = e.target.value.replace(/[^A-Za-z\s0-9.\-']/g, '');
                            setRecipient(r => ({ ...r, name_en: cleaned }));
                          }}
                          placeholder={t('الاسم بالإنجليزية فقط', 'English characters only')}
                          className="bg-[#0a0a0a] border-white/10 text-white h-11"
                          dir="ltr"
                          data-testid="recipient-name-en-input"
                        />
                      </div>
                    </div>
                  ) : (
                    <Input
                      value={recipient.name}
                      onChange={(e) => setRecipient(r => ({ ...r, name: e.target.value }))}
                      placeholder={t('اسم الشركة المستلمة', 'Recipient company name')}
                      className="bg-[#0a0a0a] border-white/10 text-white h-11"
                      data-testid="recipient-name-input"
                    />
                  )}
                </div>
              )}
            </section>

            {/* iter-217c-v3 — Two narrative boxes (AR + EN) with an inline
                translation button between them. Auto-filled from the picked
                subject's narrative_ar / narrative_en. */}
            <section className="p-5 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-semibold text-white">
                  {t('السرد داخل الشهادة (يمكنك التعديل)', 'Certificate body (editable)')} *
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3">
                <div>
                  <label className="block text-[11px] text-white/50 mb-1.5">{t('السرد بالعربية', 'Body — Arabic')}</label>
                  <textarea
                    value={bodyAr}
                    onChange={(e) => setBodyAr(e.target.value)}
                    dir="rtl"
                    rows={7}
                    className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                    data-testid="body-ar-input"
                  />
                </div>
                <div className="flex md:flex-col items-center justify-center gap-1 py-2">
                  <button
                    type="button"
                    onClick={() => translateBody('ar2en')}
                    disabled={translating['body-ar2en'] || !bodyAr.trim()}
                    title={t('ترجم من العربية إلى الإنجليزية', 'Translate AR → EN')}
                    className="w-9 h-9 rounded-md bg-blue-500/15 border border-blue-500/40 text-blue-300 hover:bg-blue-500/25 disabled:opacity-40 flex items-center justify-center"
                    data-testid="body-translate-ar2en"
                  >
                    {translating['body-ar2en'] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span className="text-[10px] font-mono">AR→EN</span>}
                  </button>
                  <button
                    type="button"
                    onClick={() => translateBody('en2ar')}
                    disabled={translating['body-en2ar'] || !bodyEn.trim()}
                    title={t('ترجم من الإنجليزية إلى العربية', 'Translate EN → AR')}
                    className="w-9 h-9 rounded-md bg-amber-500/15 border border-amber-500/40 text-amber-300 hover:bg-amber-500/25 disabled:opacity-40 flex items-center justify-center"
                    data-testid="body-translate-en2ar"
                  >
                    {translating['body-en2ar'] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span className="text-[10px] font-mono">EN→AR</span>}
                  </button>
                </div>
                <div>
                  <label className="block text-[11px] text-white/50 mb-1.5">{t('السرد بالإنجليزية', 'Body — English')}</label>
                  <textarea
                    value={bodyEn}
                    onChange={(e) => setBodyEn(e.target.value)}
                    dir="ltr"
                    rows={7}
                    className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm"
                    data-testid="body-en-input"
                  />
                </div>
              </div>
            </section>

            {/* iter-218r (Feb 2026) — Typography controls.
                Some templates render long text that overflows the single A4
                page; these three inputs let the admin tune the body font
                family, size and line-height so the text fills the page. */}
            <section className="p-5 rounded-2xl bg-white/[0.02] border border-white/10" data-testid="cert-typography-section">
              <div className="flex items-center gap-2 mb-3">
                <Type className="w-4 h-4 text-blue-300" />
                <h3 className="text-sm font-semibold text-white">
                  {t('تنسيق نص الشهادة', 'Certificate typography')}
                </h3>
                <span className="text-[10px] text-white/40 me-auto">
                  {t('تُطبَّق فوراً على المعاينة والـ PDF', 'Applied live to the preview & PDF')}
                </span>
                <button
                  type="button"
                  onClick={() => { setBodyFontFamily('Cairo'); setBodyFontSize(15); setBodyLineHeight(1.9); }}
                  className="text-[10px] px-2 py-1 rounded-md bg-white/5 border border-white/10 text-white/60 hover:text-white"
                  data-testid="cert-typography-reset"
                >
                  {t('إعادة تعيين', 'Reset')}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Font family */}
                <div>
                  <label className="block text-[11px] text-white/50 mb-1.5">
                    {t('نوع الخط', 'Font family')}
                  </label>
                  <select
                    value={bodyFontFamily}
                    onChange={(e) => setBodyFontFamily(e.target.value)}
                    className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl h-11 px-3 text-white text-sm"
                    data-testid="cert-font-family"
                  >
                    <option value="Cairo">Cairo — كايرو</option>
                    <option value="Tajawal">Tajawal — تجوّل</option>
                    <option value="Amiri">Amiri — أميري</option>
                    <option value="Noto Naskh">Noto Naskh — نسخ</option>
                    <option value="Times New Roman">Times New Roman</option>
                    <option value="Arial">Arial</option>
                  </select>
                </div>
                {/* Font size */}
                <div>
                  <label className="block text-[11px] text-white/50 mb-1.5">
                    {t(`حجم الخط: ${bodyFontSize}px`, `Font size: ${bodyFontSize}px`)}
                  </label>
                  <input
                    type="range" min="11" max="22" step="1"
                    value={bodyFontSize}
                    onChange={(e) => setBodyFontSize(Number(e.target.value))}
                    className="w-full accent-blue-500"
                    data-testid="cert-font-size"
                  />
                  <div className="flex justify-between text-[10px] text-white/40 mt-0.5">
                    <span>11</span><span>16</span><span>22</span>
                  </div>
                </div>
                {/* Line-height */}
                <div>
                  <label className="block text-[11px] text-white/50 mb-1.5">
                    {t(`المسافة بين الأسطر: ${bodyLineHeight.toFixed(1)}`,
                       `Line spacing: ${bodyLineHeight.toFixed(1)}`)}
                  </label>
                  <input
                    type="range" min="1.4" max="2.5" step="0.1"
                    value={bodyLineHeight}
                    onChange={(e) => setBodyLineHeight(Number(e.target.value))}
                    className="w-full accent-blue-500"
                    data-testid="cert-line-height"
                  />
                  <div className="flex justify-between text-[10px] text-white/40 mt-0.5">
                    <span>1.4</span><span>1.9</span><span>2.5</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Metadata */}
            <section className="p-5 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] text-white/50 mb-1.5">{t('التاريخ', 'Date')}</label>
                  <Input type="date" value={certDate} onChange={(e) => setCertDate(e.target.value)} className="bg-[#0a0a0a] border-white/10 text-white h-11" />
                </div>
                <div>
                  <label className="block text-[11px] text-white/50 mb-1.5">{t('رقم الشهادة', 'Cert #')}</label>
                  <Input value={certNo} onChange={(e) => setCertNo(e.target.value)} dir="ltr" className="bg-[#0a0a0a] border-white/10 text-white h-11 font-mono text-xs" />
                </div>
              </div>
            </section>

            {/* Actions */}
            <div className="flex items-center justify-between pt-2">
              <Button variant="outline"
                      onClick={() => navigate('/manage/cloudgm-fz-llc/certificates')}
                      className="border-white/10 text-white/80">
                {t('إلغاء', 'Cancel')}
              </Button>
              <Button
                onClick={submitForApproval}
                disabled={!canPreview || saving}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 h-11"
                data-testid="send-for-approval-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>
                  <Send className="w-4 h-4 ml-2" />
                  {t('إرسال للاعتماد', 'Send for approval')}
                </>}
              </Button>
            </div>
          </motion.div>

          {/* ── Right: PDF-styled Preview (2-column bilingual) ────── */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <div className="text-xs text-white/50 mb-2 flex items-center gap-2">
              <Award className="w-3.5 h-3.5" />
              {t('معاينة الشهادة (يتم تحويلها إلى PDF بالضبط)', 'Certificate preview (rendered as PDF exactly)')}
            </div>
            <div className="rounded-2xl overflow-hidden border border-white/10 bg-white/5">
              <div className="w-full flex justify-center bg-black/40 p-4 overflow-x-auto">
                <div
                  ref={previewRef}
                  style={{
                    width:  '794px',
                    height: '1123px',
                    background: BRAND.tint,
                    color: BRAND.ink,
                    display: 'flex',
                    flexDirection: 'column',
                    fontFamily: '"Cairo","Tajawal",-apple-system,BlinkMacSystemFont,sans-serif',
                    position: 'relative',
                  }}
                >
                  {/* Header band — logo centered, cert# top-right, date top-left */}
                  <div style={{ padding: '32px 60px 0 60px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                      <div style={{ textAlign: 'left', minWidth: 120 }}>
                        <div style={{ fontSize: 10, color: BRAND.ink400, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600 }}>Date</div>
                        <div style={{ fontSize: 13, color: BRAND.ink, fontWeight: 700, fontFamily: 'monospace', marginTop: 2 }} dir="ltr">{certDate}</div>
                      </div>
                      <img src={LOGO_URL} alt="CloudGM" style={{ height: 90, objectFit: 'contain' }} crossOrigin="anonymous" />
                      <div style={{ textAlign: 'right', minWidth: 120 }}>
                        <div style={{ fontSize: 10, color: BRAND.ink400, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600 }}>Cert #</div>
                        <div style={{ fontSize: 13, color: BRAND.ink, fontWeight: 700, fontFamily: 'monospace', marginTop: 2 }} dir="ltr">{certNo}</div>
                      </div>
                    </div>
                    <div style={{ height: 5, background: BRAND.gradient, borderRadius: 3, marginTop: 20 }} />
                  </div>

                  {/* Title (only for to_whom). For congratulations we skip the title
                      per user request — the family opening acts as the header. */}
                  {certType === 'to_whom' && (
                    <div style={{ padding: '32px 60px 8px 60px', textAlign: 'center' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
                        <div dir="ltr" style={{ minHeight: 42 }}>
                          <div style={{ fontSize: 12, color: BRAND.ink400, letterSpacing: 4, textTransform: 'uppercase', fontWeight: 600 }}>To Whom It May Concern</div>
                          <div style={{ marginTop: 8, height: 3, width: 100, background: BRAND.orange }} />
                        </div>
                        <div dir="rtl" style={{ minHeight: 42 }}>
                          <div style={{ fontSize: 22, color: BRAND.blue700, fontWeight: 800 }}>لمن يهمه الأمر</div>
                          <div style={{ marginTop: 8, height: 3, width: 100, background: BRAND.orange, marginRight: 0, marginLeft: 'auto' }} />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Two-column bilingual body: EN on the left, AR on the right */}
                  <div style={{
                    padding: certType === 'to_whom' ? '22px 60px 0 60px' : '48px 60px 0 60px',
                    flex: 1,
                    display: 'grid',
                    gridTemplateColumns: '1fr 4px 1fr',
                    gap: 32,
                  }}>
                    {/* English column (LEFT) */}
                    <div dir="ltr" style={{ fontSize: bodyFontSize, lineHeight: bodyLineHeight, color: BRAND.ink, textAlign: 'left', display: 'flex', flexDirection: 'column', fontFamily: bodyFontStack }}>
                      {/* iter-217c-v17 — Type banner has fixed height so LEFT + RIGHT
                          columns are perfectly aligned even when one language uses
                          shorter text than the other. */}
                      <div style={{ height: 22, marginBottom: 10 }}>
                        {certType === 'congratulation' && (
                          <div style={{ fontSize: 13, color: BRAND.blue700, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5 }}>
                            Congratulations
                          </div>
                        )}
                      </div>
                      {/* Recipient name banner — fixed min-height matches Arabic side */}
                      <div style={{
                        border: `2px solid ${BRAND.orange}`,
                        borderRadius: 8,
                        padding: '12px 16px',
                        marginBottom: 18,
                        minHeight: 62,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}>
                        <div style={{ fontSize: 11, color: BRAND.ink400, textTransform: 'uppercase', letterSpacing: 1 }}>Recipient</div>
                        <div style={{ fontSize: 18, color: BRAND.orange700, fontWeight: 800, marginTop: 2 }}>{(recipient.name_en || recipient.name) || '—'}</div>
                      </div>
                      <p style={{ whiteSpace: 'pre-line', margin: 0, lineHeight: bodyLineHeight }}>{bodyEn}</p>
                    </div>

                    {/* Vertical divider */}
                    <div style={{ background: BRAND.gradient, borderRadius: 2 }} />

                    {/* Arabic column (RIGHT) */}
                    <div dir="rtl" style={{ fontSize: bodyFontSize + 1, lineHeight: bodyLineHeight, color: BRAND.ink, textAlign: 'right', display: 'flex', flexDirection: 'column', fontFamily: bodyFontStack }}>
                      <div style={{ height: 22, marginBottom: 10 }}>
                        {certType === 'congratulation' && (
                          <div style={{ fontSize: 14, color: BRAND.blue700, fontWeight: 700, letterSpacing: 1 }}>
                            تهنئة
                          </div>
                        )}
                      </div>
                      <div style={{
                        border: `2px solid ${BRAND.orange}`,
                        borderRadius: 8,
                        padding: '12px 16px',
                        marginBottom: 18,
                        minHeight: 62,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}>
                        <div style={{ fontSize: 11, color: BRAND.ink400 }}>المستلم</div>
                        <div style={{ fontSize: 18, color: BRAND.orange700, fontWeight: 800, marginTop: 2 }}>{(recipient.name_ar || recipient.name) || '—'}</div>
                      </div>
                      <p style={{ whiteSpace: 'pre-line', margin: 0, lineHeight: bodyLineHeight }}>{bodyAr}</p>
                    </div>
                  </div>

                  {/* Signature strip — iter-218j (Feb 2026)
                      Two-column layout: LEFT = signature+name+title+company,
                      RIGHT = large official stamp. Preview reserves space
                      but doesn't render approved data during draft creation. */}
                  <div dir="ltr" style={{
                    padding: '32px 60px 0 60px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
                    gap: 24, minHeight: 220,
                  }}>
                    {/* LEFT column */}
                    <div style={{ flex: '0 0 auto', minWidth: 240, maxWidth: 380 }}>
                      {approvedSignatureImg ? (
                        <img
                          src={approvedSignatureImg}
                          alt="signature"
                          style={{
                            display: 'block', maxHeight: 80, maxWidth: 260,
                            objectFit: 'contain', marginBottom: 2,
                          }}
                          crossOrigin="anonymous"
                        />
                      ) : (
                        <div style={{ height: 60 }} />
                      )}
                      <div style={{
                        borderTop: `1.5px dashed ${BRAND.ink400}`,
                        width: '80%', maxWidth: 240, marginBottom: 8,
                      }} />
                      {/* Placeholder company (real signer name/title fill at approve time) */}
                      <div style={{ paddingTop: 4 }}>
                        <div dir="ltr" style={{
                          fontSize: 14, color: BRAND.ink, fontWeight: 800,
                          letterSpacing: 0.4, lineHeight: 1.35,
                        }}>
                          {company.name_en || 'CLOUDGM FZ-LLC'}
                        </div>
                        {company.name_ar && (
                          <div dir="rtl" style={{
                            fontSize: 13, color: BRAND.ink, fontWeight: 700,
                            lineHeight: 1.35, textAlign: 'left',
                          }}>
                            {company.name_ar}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* RIGHT column: stamp */}
                    <div style={{
                      flex: '0 0 auto', display: 'flex',
                      alignItems: 'flex-end', justifyContent: 'flex-end',
                    }}>
                      {approvedStampImg && (
                        <img
                          src={approvedStampImg}
                          alt="official company stamp"
                          style={{
                            maxHeight: 200, maxWidth: 220, objectFit: 'contain',
                            opacity: 0.95,
                          }}
                          crossOrigin="anonymous"
                        />
                      )}
                    </div>
                  </div>

                  {/* Footer band — iter-218 (Feb 2026):
                        - Website icon next to sites
                        - Mail icon next to emails
                        - WhatsApp icon next to phone
                        - Phone tightened (letter-spacing 0) */}
                  <div style={{
                    marginTop: 28,
                    background: BRAND.gradient,
                    color: '#fff',
                    padding: '14px 60px',
                    fontSize: 12,
                    fontWeight: 600,
                  }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1.3fr 1fr 1.3fr',
                      gap: 20,
                      alignItems: 'center',
                    }} dir="ltr">
                      {/* Left cell: websites (with globe icon) */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, lineHeight: 1.5 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                            <circle cx="12" cy="12" r="10" />
                            <path d="M2 12h20" />
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10Z" />
                          </svg>
                          cloudgm.com
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                            <circle cx="12" cy="12" r="10" />
                            <path d="M2 12h20" />
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10Z" />
                          </svg>
                          cloudgm.business
                        </span>
                      </div>
                      {/* Middle cell: phone + WhatsApp icon */}
                      <div style={{
                        textAlign: 'center',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        fontSize: 14,
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                        letterSpacing: 0,
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff" style={{ flexShrink: 0 }}>
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.464 3.488"/>
                        </svg>
                        {CONTACT.phone}
                      </div>
                      {/* Right cell: emails (with mail icon) */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'right', lineHeight: 1.5 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                            <rect x="2" y="4" width="20" height="16" rx="2" />
                            <path d="m22 7-10 5L2 7" />
                          </svg>
                          info@cloudgm.com
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                            <rect x="2" y="4" width="20" height="16" rx="2" />
                            <path d="m22 7-10 5L2 7" />
                          </svg>
                          info@cloudgm.business
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-3 flex items-start gap-2 p-3 rounded-xl bg-blue-500/5 border border-blue-500/20">
              <CheckCircle2 className="w-4 h-4 text-blue-300 mt-0.5" />
              <p className="text-xs text-blue-100/80">
                {t('العمود الأيمن بالعربية والعمود الأيسر بالإنجليزية. عند اختيار الموضوع سيُعبَّأ السرد تلقائياً ويمكنك التعديل.',
                   'The right column is Arabic and the left is English. Picking a subject auto-fills the body — you can still edit it.')}
              </p>
            </div>
          </motion.div>
        </div>
        )}
      </main>
    </DashboardLayout>
  );
}
